import React, { Component, PropTypes } from 'react';
import Tappable from 'react-tappable';
import omit from 'lodash.omit';
import pick from 'lodash.pick';
import isEqual from 'lodash.isequal';
import { navigate } from 'store/location/location';
import isNewTabEvent from 'is-new-tab-event';
import { provideQualifiedLinkParams } from 'qualified-location';

const isRightClickPressed = ({ nativeEvent }) => nativeEvent.type === 'contextmenu' || nativeEvent.which === 3;

const visualProps = [
  'aria-checked',
  'className',
  'path',
  'data-automation',
  'disabled'
];

// Create small recursive props hierarchy with only visual props
function visualPropsTree(component) {
  if(Array.isArray(component)) {
    return component.map(visualPropsTree);
  }

  if(!component || !component.props) {
    return component;
  }

  return {
    ...pick(component.props, visualProps),
    children: visualPropsTree(component.props.children)
  };
}

function preventDefaultIfNotNewTab(event) {
  if(isNewTabEvent(event)) {
    return true; // open new tab
  }

  event.preventDefault();
  return false;
}

@provideQualifiedLinkParams
export default class QualifiedLink extends Component {

  static propTypes = {
    path: PropTypes.string,
    params: PropTypes.object.isRequired,
    state: PropTypes.object,
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.node
    ]),
    dispatch: PropTypes.func.isRequired,
    getQualifiedLinkParams: PropTypes.func.isRequired,
    analytics: PropTypes.string,
    onClick: PropTypes.func,
    scrollToTop: PropTypes.bool,
    preNavigationAction: PropTypes.func
  };

  static defaultProps = {
    path: null,
    params: {},
    state: {},
    analytics: '',
    scrollToTop: false
  };

  constructor(props) {
    super(props);
    this.handleClick = this.handleClick.bind(this);
    this.ensureUpdatedHrefForContextMenu = this.ensureUpdatedHrefForContextMenu.bind(this);
  }

  // Need to track mount status see forceUpdateIfMounted()
  componentDidMount() {
    this.componentIsMounted = true;
  }

  shouldComponentUpdate(newProps) {
    return !isEqual(visualPropsTree(newProps), visualPropsTree(this.props));
  }

  // Need to track mount status see forceUpdateIfMounted()
  componentWillUnmount() {
    this.componentIsMounted = false;
  }

  ensureUpdatedHrefForContextMenu() {
    // tracking mount status avoids errors when
    // forceUpdate() is run on unmounted component
    // which happens while components animate in.
    if(this.componentIsMounted) {
      this.forceUpdate();
    }
  }

  handleClick(event) {
    const {
      analytics,
      dispatch,
      onClick,
      path,
      params,
      preNavigationAction,
      scrollToTop,
      state
    } = this.props;

    event.preventDefault();

    // Cancel navigation if trying to access context menu or open in new tab
    if(isRightClickPressed(event) || isNewTabEvent(event)) {
      return;
    }

    // Actually do a navigate
    if (scrollToTop === true && !isNewTabEvent(event)) {
      document.body.scrollIntoView();
    }

    if (typeof onClick === 'function') {
      onClick(event);
    }

    dispatch(navigate({
      analytics,
      params,
      path,
      preNavigationAction,
      state
    }));
  }

  render() {
    const {
      state,
      path,
      params,
      getQualifiedLinkParams,
      ...restProps
    } = this.props;

    const props = omit(
      restProps,
      'analytics',
      'dispatch',
      'onClick', // don't allow onClick override
      'onTap', // don't allow onTap override
      'preNavigationAction',
      'scrollToTop',
    );

    return (
      <Tappable
        component="a"
        onTap={this.handleClick}
        onClick={preventDefaultIfNotNewTab}
        onMouseOver={this.ensureUpdatedHrefForContextMenu}
        {...getQualifiedLinkParams(path, params, state)}
        {...props}
      />
    );
  }

}



/** WEBPACK FOOTER **
 ** ./app/components/QualifiedLink/QualifiedLink.js
 **/